import { ReactNode, useEffect, useMemo } from "react";

import FeatureModuleFallbackModal from "modules/user/components/FeatureModuleFallbackModal";

import { useBoolean } from "modules/core/hooks/useBoolean";
import { useAuthUserQuery } from "modules/user/hooks/queries/useAuthUserQuery";
import { useSelectedProject } from "modules/projects/hooks/useSelectedProject";
import { useFeatureModulesQuery } from "modules/user/hooks/queries/useFeatureModulesQuery";

import { canAccess } from "./canAccess";
import { coreRoutesConstants } from "modules/core/routes/routesConstants";

import { UserFeatureModuleKey } from "modules/user/types/entities";

interface IUserFeatureProps {
  featureKey: UserFeatureModuleKey;
  fallback_url?: string;
  url?: string;
  redirect?: boolean;
  projectId?: string;
  withFallbackModal?: boolean;
  fallbackModalPosition?: "absolute" | "fixed";
  closableFallbackModal?: boolean;
  children:
    | ((args: {
        url?: string;
        hasAccess?: boolean;
        isFallbackModalOpen: boolean;
        openFallbackModal: () => void;
      }) => ReactNode)
    | ReactNode;
}

const UserFeature = (props: IUserFeatureProps) => {
  const {
    children,
    featureKey,
    withFallbackModal = false,
    fallbackModalPosition = "absolute",
    closableFallbackModal = true
  } = props;

  const { final_url, hasAccess } = useUserFeatureModule(props);

  const [
    isFallbackModalOpen,
    { on: openFallbackModal, off: closeFallbackModal }
  ] = useBoolean(withFallbackModal);

  if (typeof children === "function")
    return (
      <>
        {children({
          url: final_url,
          hasAccess,
          isFallbackModalOpen,
          openFallbackModal
        })}

        {isFallbackModalOpen && !hasAccess && (
          <FeatureModuleFallbackModal
            featureKey={featureKey}
            onClose={closableFallbackModal ? closeFallbackModal : undefined}
            isAbsolutePosition={fallbackModalPosition === "absolute"}
          />
        )}
      </>
    );

  if (hasAccess) return <>{children}</>;

  if (withFallbackModal)
    return (
      <>
        {children}

        <FeatureModuleFallbackModal
          featureKey={featureKey}
          isAbsolutePosition={fallbackModalPosition === "absolute"}
        />
      </>
    );

  return null;
};

export const useUserFeatureModule = (
  args: Omit<IUserFeatureProps, "children" | "withModalFallback">
) => {
  const { featureKey, fallback_url, url, redirect, projectId } = args;

  const { isSingleProject, selectedProject } = useSelectedProject();
  const { data: userData } = useAuthUserQuery();

  const { data: features } = useFeatureModulesQuery();

  const hasAccess = useMemo(() => {
    if (features) {
      return canAccess({
        features,
        key: featureKey,
        isBetaUser: !!userData?.beta,
        projectId: projectId || (isSingleProject ? selectedProject?.id : null)
      });
    }

    return false;
  }, [
    featureKey,
    features,
    isSingleProject,
    projectId,
    selectedProject,
    userData
  ]);

  useEffect(() => {
    if (redirect && !hasAccess) {
      window.location.href =
        fallback_url || coreRoutesConstants.dashboard.href("all");
    }
  }, [fallback_url, hasAccess, redirect]);

  return {
    final_url: hasAccess ? url : fallback_url,
    hasAccess
  };
};

export type UserAccesses = { [key in UserFeatureModuleKey]: boolean };

const allModules = Object.values(UserFeatureModuleKey);

export const useUserFeatureAccesses = ({
  featureKeys,
  projectId
}: {
  featureKeys?: UserFeatureModuleKey[];
  projectId?: string;
} = {}): UserAccesses => {
  const { isSingleProject, selectedProject } = useSelectedProject();

  const { data: features } = useFeatureModulesQuery();
  const { data: userData } = useAuthUserQuery();

  const keys = featureKeys || allModules;

  const accesses = useMemo(() => {
    if (features) {
      return keys.reduce(
        (base, key) => ({
          ...base,
          [key]: canAccess({
            features,
            key,
            isBetaUser: !!userData?.beta,
            projectId:
              projectId || (isSingleProject ? selectedProject?.id : null)
          })
        }),
        {} as UserAccesses
      );
    }

    return keys.reduce(
      (base, curr) => ({
        ...base,
        [curr]: false
      }),
      {} as UserAccesses
    );
  }, [
    features,
    keys,
    userData,
    projectId,
    isSingleProject,
    selectedProject?.id
  ]);

  return accesses;
};

export default UserFeature;
